import { QueryClient, type QueryKey } from "@tanstack/query-core";
import type { RateLimits } from "../problems/rateLimit.ts";
import { keys } from "./keys.ts";

/**
 * `contracts/client-api.md` § Defaults, `research.md` W-04: `staleTime` 30 seconds, `gcTime`
 * 15 minutes, `refetchOnWindowFocus` false, `refetchOnReconnect` false, structural sharing
 * on. The cache does not refetch on reconnect by itself because the offline machine owns that
 * (§ Offline detection and the write queue): the connection is confirmed by the probe, the
 * write queue drains, and only then are the active queries read, once each (`../runtime.ts`). Retry is the transport's (§ Retry), so the cache never retries a second time,
 * and that includes `retryOnMount` off: a read that failed is not asked again because another
 * component mounted it, only when something invalidates it or a person asks with Retry. With it
 * on, a refused read whose pending state unmounted the screen that mounted it asked again on every
 * remount, fifteen times a second for a guest's inline composer (2026-09-29). Three
 * families deviate: the instance read at 10 minutes, search at 0 seconds and not garbage
 * collected while the palette is open, and the unread count at infinity because it is
 * patched rather than re-read.
 */
export const DEFAULT_STALE_TIME = 30_000;
export const DEFAULT_GC_TIME = 15 * 60_000;
export const INSTANCE_STALE_TIME = 10 * 60_000;

export interface QueryClientOptions {
  readonly rateLimits?: RateLimits;
}

export interface BatonQueryClient extends QueryClient {
  /** The palette's open state, which keeps the search family out of garbage collection. */
  setPaletteOpen(open: boolean): void;
  /** Apply the search family's defaults for a workspace once it is known. */
  registerWorkspace(ws: string): void;
}

export function createQueryClient(options: QueryClientOptions = {}): BatonQueryClient {
  let paletteOpen = false;
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        gcTime: DEFAULT_GC_TIME,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
        retryOnMount: false,
        structuralSharing: true,
        ...(options.rateLimits ? { enabled: (query: { queryKey: QueryKey }) => !options.rateLimits?.isPaused(query.queryKey) } : {}),
      },
      mutations: { retry: false },
    },
  }) as BatonQueryClient;

  client.setQueryDefaults(keys.global.instance(), { staleTime: INSTANCE_STALE_TIME });

  const registered = new Set<string>();
  const applySearchDefaults = () => {
    for (const ws of registered) client.setQueryDefaults(keys.searches(ws), { staleTime: 0, gcTime: paletteOpen ? Infinity : DEFAULT_GC_TIME });
  };
  client.registerWorkspace = (ws) => {
    if (registered.has(ws)) return;
    registered.add(ws);
    applySearchDefaults();
    client.setQueryDefaults(keys.unreadCount(ws), { staleTime: Infinity });
  };
  client.setPaletteOpen = (open) => {
    paletteOpen = open;
    applySearchDefaults();
  };
  return client;
}
